import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootReducerState } from '../../redux/Reducers';
import {
  CellTowerReportAddressType,
  CellTowerReportListResponseType,
  CellTowerReportResultsResponseType,
} from '../../types/cell-towers/cell-tower-report.type';
import { AsyncTaskType } from '../../types/async-task/async-task.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { rejectWithApiError } from '../../utils/api';
import { additionalQueryParams } from '../../utils/utilities';

type CellTowerReportResultsExportScope = 'current' | 'full';

export const getCellTowerReportsList = createAsyncThunk<
  CellTowerReportListResponseType,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState; rejectValue: string }
>('cellTowerReports/getCellTowerReportsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      null,
      thunkAPI.getState().sharedReducer.includeSuborgs
    );

    const response = await axios.get<CellTowerReportListResponseType>(
      `${process.env.REACT_APP_API_URL}/cell-towers/address-analyses`,
      {
        params: {
          ...params,
          ...additionalParams,
        },
        ...(controller ? { signal: controller.signal } : {}),
      }
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not fetch cell tower reports!' });
  }
});

export const postCellTowerReport = createAsyncThunk<
  AsyncTaskType,
  { addresses: CellTowerReportAddressType[]; maxTowers: number; radiusMiles: number },
  { rejectValue: string }
>('cellTowerReports/postCellTowerReport/no-loader', async (data, thunkAPI) => {
  try {
    const response = await axios.post<AsyncTaskType>(
      `${process.env.REACT_APP_API_URL}/cell-towers/address-bulk-uploads`,
      data
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not start cell tower report!' });
  }
});

export const getCellTowerReportResults = createAsyncThunk<
  CellTowerReportResultsResponseType,
  RouteParamsType & { reportId: number; controller?: AbortController },
  { rejectValue: string }
>('cellTowerReports/getCellTowerReportResults/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, reportId, ...params } = data;
    const cleanedParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    );
    const response = await axios.get<CellTowerReportResultsResponseType>(
      `${process.env.REACT_APP_API_URL}/cell-towers/address-analyses/${reportId}/results`,
      {
        params: cleanedParams,
        ...(controller ? { signal: controller.signal } : {}),
      }
    );

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not fetch cell tower report results!' });
  }
});

export const getCellTowerReportAsyncTask = createAsyncThunk<AsyncTaskType, number, { rejectValue: string }>(
  'cellTowerReports/getCellTowerReportAsyncTask/no-loader',
  async (reportId, thunkAPI) => {
    try {
      const response = await axios.get<AsyncTaskType>(
        `${process.env.REACT_APP_API_URL}/cell-towers/address-analyses/${reportId}/status`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Could not fetch cell tower report status!' });
    }
  }
);

export const downloadCellTowerReportResults = createAsyncThunk<
  void,
  { reportId: number; routeParams: RouteParamsType; scope: CellTowerReportResultsExportScope },
  { rejectValue: string }
>('cellTowerReports/downloadCellTowerReportResults/no-loader', async ({ reportId, routeParams, scope }, thunkAPI) => {
  try {
    const response = await axios.get<Blob>(
      `${process.env.REACT_APP_API_URL}/cell-towers/address-analyses/${reportId}/results/export`,
      {
        params: {
          ...routeParams,
          scope,
        },
        responseType: 'blob',
      }
    );
    const file = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const contentDisposition = response.headers['content-disposition'];
    const filenameMatch = /filename="([^"]+)"/.exec(contentDisposition || '');

    link.href = URL.createObjectURL(file);
    link.setAttribute('download', filenameMatch?.[1] || `cell-tower-report-${reportId}-${scope}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not download cell tower report results!' });
  }
});
